import { isNotEmpty, useForm } from '@mantine/form';
import isEqual from 'lodash/isEqual';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateMilestoneMutation,
  useDeleteMilestoneMutation,
  useUpdateMilestoneMutation,
} from '../../../../../api/milestones-client/milestones-client.hooks';
import type { MilestonePayload } from '../../../../../api/milestones-client/milestones-client.type';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import { useDeepCompareEffect } from '../../../../../hooks/useDeepCompareEffect';
import { useMilestonesTabStore, useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { setMilestonesData, setSelectedMilestone } from '../../../../../store/strategy-store/strategy-store.actions';
import { skyBase } from '../../../../../styles/design-tokens';
import { Text } from '../../../../../ui-library/typography/typography';
import { toast } from '../../../../notifications';
import { StyledModal } from '../../../strategy-entity-modal/strategy-entity-modal';
import { MilestoneModalStepOne } from './milestone-modal-step-one';
import { MilestoneModalStepTwo } from './milestone-modal-step-two';
import type { MilestoneFormData, MilestoneModalProps } from './milestone-modal.type';

export function MilestoneModal({ mode, milestone, outcome, opened, onClose }: MilestoneModalProps) {
  const i18n = useTranslation();
  const initiativePerformance = useStrategyStore((state) => state.initiativePerformance);
  const milestones = useMilestonesTabStore((state) => state.milestones);
  const selectedMilestone = useMilestonesTabStore((state) => state.selectedMilestone);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<MilestoneFormData>>({});
  const [selectedEpics, setSelectedEpics] = useState<Epic[]>(milestone && milestone.epics ? milestone.epics : []);

  // Initialize mutations
  const createMutation = useCreateMilestoneMutation({
    onSuccess: (data) => {
      const updatedMilestones = [...milestones, data];
      setMilestonesData(updatedMilestones);
      toast.success(i18n.t('strategy.milestones.created_success', { defaultValue: 'Milestone created successfully' }));
      onClose();
      setTimeout(resetModalState, 300);
    },
    onError: () => {
      toast.error(i18n.t('strategy.milestones.created_error', { defaultValue: 'Failed to create milestone' }));
    },
  });

  const updateMutation = useUpdateMilestoneMutation({
    onSuccess: (data) => {
      const updatedMilestones = milestones.map((m) => (m.id === data.id ? data : m));
      setMilestonesData(updatedMilestones);
      setSelectedMilestone(data);
      toast.success(i18n.t('strategy.milestones.updated_success', { defaultValue: 'Milestone updated successfully' }));
      onClose();
      setTimeout(resetModalState, 300);
    },
    onError: () => {
      toast.error(i18n.t('strategy.milestones.updated_error', { defaultValue: 'Failed to update milestone' }));
    },
  });

  const deleteMutation = useDeleteMilestoneMutation({
    onSuccess: () => {
      const updatedMilestones = milestones.filter((m) => m.id !== milestone?.id);
      setMilestonesData(updatedMilestones);
      if (selectedMilestone?.id === milestone?.id) {
        setSelectedMilestone(updatedMilestones[0] || null);
      }
      toast.success(i18n.t('strategy.milestones.deleted_success', { defaultValue: 'Milestone deleted successfully' }));
      onClose();
      setTimeout(resetModalState, 300);
    },
    onError: () => {
      toast.error(i18n.t('strategy.milestones.deleted_error', { defaultValue: 'Failed to delete milestone' }));
    },
  });

  const initialValues = {
    name: milestone?.name || '',
    description: milestone?.description || '',
    target_end_date: milestone?.target_end_date || outcome?.end_date || '',
  };

  const form = useForm<Partial<MilestoneFormData>>({
    initialValues,
    validate: {
      name: isNotEmpty('Name is required'),
      target_end_date: (value) => {
        if (!value) {
          return 'End date is required';
        }
      },
    },
  });

  useDeepCompareEffect(() => {
    const currentValues = form.values;
    const hasChanges = !isEqual(currentValues, initialValues);

    if (hasChanges) {
      form.setValues(initialValues);
      form.resetDirty();
    }
  }, [initialValues]);

  useDeepCompareEffect(() => {
    const hasChanges = !isEqual(milestone?.epics || [], selectedEpics);

    if (hasChanges) {
      setSelectedEpics(milestone?.epics || []);
    }
  }, [milestone?.epics]);

  const resetModalState = () => {
    setStep(1);
    setFormData({});
    form.reset();
    setSelectedEpics(milestone?.epics || []);
  };

  const handleClose = () => {
    const userConfirmed = window.confirm(i18n.t('strategy.initiatives.unsaved_changes'));

    if (userConfirmed) {
      onClose();
      setTimeout(resetModalState, 300);
    }
  };

  const handleStepOneNext = (data: Partial<MilestoneFormData>) => {
    if (!form.validate().hasErrors) {
      setFormData({ ...formData, ...data });
      setStep(2);
    }
  };

  const handleStepTwoBack = () => {
    setStep(1);
  };

  const handleStepTwoSubmit = async () => {
    if (selectedEpics.length === 0) {
      alert(i18n.t('strategy.milestones.validation.at_least_one_epic'));
      return;
    }

    const submitData: Partial<MilestonePayload> = {
      ...formData,
      epics: selectedEpics.map((e: Epic) => e.id),
      outcome: outcome?.id ?? initiativePerformance?.id ?? '',
    };

    if (mode === 'edit' && milestone?.id) {
      await updateMutation.mutateAsync({
        milestoneId: milestone.id,
        data: submitData,
      });
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  const getTitle = () => {
    const action = mode === 'create' ? 'Create' : 'Edit';
    return `${action} Milestone`;
  };

  const title = (
    <Text size="large" weight="bold">
      {getTitle()} <span style={{ color: skyBase }}>{i18n.t('strategy.initiatives.step_indicator', { step })}</span>
    </Text>
  );

  const handleDeleteConfirm = async () => {
    if (milestone?.id) {
      await deleteMutation.mutateAsync(milestone.id);
    }
  };

  return (
    <StyledModal size={580} opened={opened} onClose={handleClose} title={title} centered>
      {step === 1 && (
        <MilestoneModalStepOne
          mode={mode}
          form={form}
          onNext={handleStepOneNext}
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          isDeleting={deleteMutation.isPending}
        />
      )}
      {step === 2 && (
        <MilestoneModalStepTwo
          mode={mode}
          selectedEpics={selectedEpics}
          setSelectedEpics={setSelectedEpics}
          availableEpics={outcome?.epics || []}
          onBack={handleStepTwoBack}
          onCancel={handleClose}
          onSubmit={handleStepTwoSubmit}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </StyledModal>
  );
}
